import React, { useState } from 'react';
import { solicitarAplazamiento } from '../../services/aplazamientoService';
import { formatearFecha } from '../MiZona/estadosPartido';
import ModalResumen from './ModalResumen';
import PartidoVs from './PartidoVs';
import {
  archivoABase64, AVISO_REVISION_COMITE, CORREO_COMITE, jornadasDisponibles, partidosDeJornada, textoContrincante, validarArchivo,
} from './aplazamientoUtils';

// Formulario de nueva solicitud. Jornada → modalidad → partido (texto). «Enviar»
// abre la modal de resumen; «Solicitar» manda todo a la API.
export default function SolicitarAplazamiento({ solicitables, plazoDias, jugadorId, onSolicitado }) {
  const [jornada, setJornada] = useState('');
  const [partidoId, setPartidoId] = useState('');
  const [tieneJustificante, setTieneJustificante] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [admiteWo, setAdmiteWo] = useState(false);
  const [error, setError] = useState(null);
  const [resumenAbierto, setResumenAbierto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState(null);
  const [exito, setExito] = useState(false);
  const [sinCorreo, setSinCorreo] = useState(false);
  // El partido se conserva al enviar: el listado se recarga y ya no lo incluye.
  const [enviado, setEnviado] = useState(null);

  const jornadas = jornadasDisponibles(solicitables);
  const delaJornada = partidosDeJornada(solicitables, jornada);
  const partido = solicitables.find((p) => String(p.partidoId) === String(partidoId)) ?? null;

  const cambiarJornada = (valor) => {
    setJornada(valor);
    const candidatos = partidosDeJornada(solicitables, valor);
    setPartidoId(candidatos.length === 1 ? String(candidatos[0].partidoId) : '');
  };

  const abrirResumen = (e) => {
    e.preventDefault();
    if (!partido) { setError('Elige la jornada y la modalidad.'); return; }
    const problema = tieneJustificante ? validarArchivo(archivo) : null;
    if (problema) { setError(problema); return; }
    setError(null);
    setErrorEnvio(null);
    setResumenAbierto(true);
  };

  const solicitar = async () => {
    setEnviando(true);
    setErrorEnvio(null);
    try {
      const justificante = tieneJustificante
        ? { nombre: archivo.name, tipo: archivo.type, contenido: await archivoABase64(archivo) }
        : null;
      const r = await solicitarAplazamiento({ partidoId: partido.partidoId, admiteWo: admiteWo && !tieneJustificante, tieneJustificante, justificante, jugadorId });
      setSinCorreo(r?.correoEnviado === false);
      setEnviado(partido);
      setExito(true);
      onSolicitado();
    } catch (err) {
      setErrorEnvio(err.message || 'No se pudo solicitar el aplazamiento.');
    } finally {
      setEnviando(false);
    }
  };

  const cerrarExito = () => {
    setResumenAbierto(false);
    setExito(false);
    setEnviado(null);
    setJornada('');
    setPartidoId('');
    setTieneJustificante(false);
    setArchivo(null);
    setAdmiteWo(false);
  };

  if (solicitables.length === 0 && !exito) {
    return <p className="mz-vacio">No tienes partidos pendientes que se puedan aplazar ahora mismo.</p>;
  }

  const mostrado = enviado ?? partido;

  return (
    <>
      <form className="ap-form" onSubmit={abrirResumen}>
        <div className="ap-campo">
          <label htmlFor="ap-jornada">Jornada</label>
          <select id="ap-jornada" value={jornada} onChange={(e) => cambiarJornada(e.target.value)}>
            <option value="">— elige —</option>
            {jornadas.map((j) => (
              <option key={j.jornada} value={j.jornada}>{j.jornada} ({formatearFecha(j.fechaJornada)})</option>
            ))}
          </select>
        </div>

        <div className="ap-campo">
          <label htmlFor="ap-modalidad">Modalidad</label>
          <select id="ap-modalidad" value={partidoId} disabled={jornada === ''} onChange={(e) => setPartidoId(e.target.value)}>
            <option value="">— elige —</option>
            {delaJornada.map((p) => <option key={p.partidoId} value={p.partidoId}>{p.modalidad}</option>)}
          </select>
        </div>

        <div className="ap-campo">
          <span className="ap-etiqueta">Partido</span>
          <span className="ap-valor">{partido ? <PartidoVs partido={partido} /> : '—'}</span>
        </div>

        <fieldset className="ap-campo ap-radios">
          <legend>Tengo justificante</legend>
          <label><input type="radio" name="ap-justificante" checked={tieneJustificante} onChange={() => { setTieneJustificante(true); setAdmiteWo(false); }} /> Sí</label>
          <label><input type="radio" name="ap-justificante" checked={!tieneJustificante} onChange={() => { setTieneJustificante(false); setArchivo(null); }} /> No</label>
        </fieldset>

        {tieneJustificante && (
          <div className="ap-campo">
            <label htmlFor="ap-archivo">Justificante (PDF o imagen, máx. 5 MB)</label>
            <input id="ap-archivo" type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => setArchivo(e.target.files?.[0] ?? null)} />
          </div>
        )}

        {/* Con justificante decide el comité: no se puede admitir WO a la vez. */}
        {!tieneJustificante && (
          <label className="ap-check">
            <input type="checkbox" checked={admiteWo} onChange={(e) => setAdmiteWo(e.target.checked)} /> Admites WO
          </label>
        )}

        <p className="ap-banner" role="note">
          {tieneJustificante && <><strong>{AVISO_REVISION_COMITE}</strong>{' '}</>}
          {admiteWo ? (
            'Si admites WO, se te puntuará como incomparecencia según el reglamento de bádminton: 0 puntos si avisas con al menos 7 días de antelación y -1 punto si avisas con menos. No se aplazará tu partido.'
          ) : (
            <>
              Si no admites WO, tendrás un plazo hasta {plazoDias} días siguientes al día del partido. Tú deberás hablar con{' '}
              {textoContrincante(partido)}, acordar lugar y hora para jugar el partido pendiente, reservar la pista por tu cuenta e
              informarnos, dentro del mismo plazo de {plazoDias} días, los puntos por la Comunidad de WhatsApp o por correo{' '}
              <a href={`mailto:${CORREO_COMITE}`}>{CORREO_COMITE}</a>. Consejo: puedes recuperarlo semanas antes de ese día, no hay plazo hacia atrás.
            </>
          )}
        </p>

        {error && <p className="msg-error" role="alert">{error}</p>}
        <button type="submit" className="btn btn-primary">Enviar</button>
      </form>

      {resumenAbierto && mostrado && (
        <ModalResumen
          partido={mostrado}
          admiteWo={admiteWo}
          conJustificante={tieneJustificante}
          fechaLimite={mostrado.fechaLimite}
          enviando={enviando}
          error={errorEnvio}
          exito={exito}
          sinCorreo={sinCorreo}
          onRevisar={() => setResumenAbierto(false)}
          onSolicitar={solicitar}
          onCerrar={cerrarExito}
        />
      )}
    </>
  );
}
